import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// Wire schema for `GET /api/v1/members/me/profile`. A server-composed view of
// the member's onboarding answers: the preferred name plus display-ready goal
// rows whose copy is resolved server-side (substance variance and localization
// both live on the server — the client renders the strings verbatim and maps
// only `kind` to presentation).

export const MEMBER_PROFILE_GOAL_ROW_KIND = ['goal', 'motivation'] as const;
export const memberProfileGoalRowKindSchema = z.enum(MEMBER_PROFILE_GOAL_ROW_KIND);
export type MemberProfileGoalRowKind = z.infer<typeof memberProfileGoalRowKindSchema>;

export const memberProfileGoalRowSchema = z
  .object({
    kind: memberProfileGoalRowKindSchema,
    title: z.string().min(1),
    subtitle: z.string().min(1),
  })
  .strict();
export type MemberProfileGoalRow = z.infer<typeof memberProfileGoalRowSchema>;

export const memberProfileDataSchema = z
  .object({
    // Blank names degrade to null server-side; the wire contract rejects ""
    // so a missed degradation can't pass either parse boundary.
    preferredName: z.string().min(1).nullable(),
    // At most one row per kind: a duplicate would mean the server composed
    // the same answer twice, so it fails validation rather than rendering.
    goals: z
      .array(memberProfileGoalRowSchema)
      .max(MEMBER_PROFILE_GOAL_ROW_KIND.length)
      .refine((rows) => new Set(rows.map((row) => row.kind)).size === rows.length, {
        message: 'goals must contain at most one row per kind',
      }),
  })
  .strict();
export type MemberProfileData = z.infer<typeof memberProfileDataSchema>;

export const memberProfileResponseSchema = apiEnvelope(memberProfileDataSchema);
export type MemberProfileResponse = z.infer<typeof memberProfileResponseSchema>;
